/**
 * Phase 114.B — Per-Student Rubric Panel.
 *
 * Opens when the teacher clicks a Gradebook cell for a rubric-shaped
 * (multi-criteria, level-scale) assessment. This is the ONE surface
 * in Atomic Edu that is deliberately UNBRANDED — it potentially goes
 * home to parents, so no slate-navy chrome, no mint accents, no logo.
 * Montserrat throughout for typography consistency only.
 *
 * Key invariants (per spec — see emergent_prompt_rubric_panel.md):
 * - Overall mark is the teacher's HOLISTIC OVERRIDE, never auto-averaged
 *   from criterion levels. The criteria inform judgment, they don't
 *   compute it.
 * - Criterion levels + overall mark + feedback text auto-save.
 * - AI feedback grounds in the SPECIFIC levels marked (parent vs
 *   student tone). Draft drops into the editable feedback box —
 *   teacher edits, final comment is theirs.
 * - PDF export is also unbranded.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Download, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  generateGradebookFeedback,
  getGradebookSingleResult,
  gradebookRubricPdfUrl,
  upsertGradebookResult,
} from "../lib/api";

// The rubric panel only offers the four pure Ontario levels — granular
// pluses/minuses live on the overall holistic mark, where the teacher
// can pick from the existing 13-step scale via the dropdown below the
// criteria rows.
const RUBRIC_LEVELS = ["1", "2", "3", "4"];
const ONTARIO_GRANULAR = [
  "R",
  "1-",
  "1",
  "1+",
  "2-",
  "2",
  "2+",
  "3-",
  "3",
  "3+",
  "4-",
  "4",
  "4+",
];

const debounce = (fn, wait) => {
  let t;
  const debounced = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(t);
  return debounced;
};

export default function RubricPanel({
  open,
  onOpenChange,
  assessment,
  studentLabel,
  studentNumber,
  onSaved,
}) {
  const [loading, setLoading] = useState(true);
  const [criterionLevels, setCriterionLevels] = useState({});
  const [overallMark, setOverallMark] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTone, setAiTone] = useState("parent");
  const lastSavedRef = useRef({ criterionLevels: {}, overallMark: "", feedbackText: "" });

  // Hydrate from backend on open.
  useEffect(() => {
    if (!open || !assessment?.id || !studentNumber) return;
    let cancelled = false;
    setLoading(true);
    getGradebookSingleResult(assessment.id, studentNumber)
      .then((res) => {
        if (cancelled) return;
        const r = res?.result || null;
        const cl = (r && r.criterion_levels) || {};
        const om = (r && r.mark) || "";
        const fb = (r && r.feedback_text) || "";
        setCriterionLevels(cl);
        setOverallMark(om);
        setFeedbackText(fb);
        lastSavedRef.current = { criterionLevels: cl, overallMark: om, feedbackText: fb };
      })
      .catch(() => {
        if (cancelled) return;
        // No prior data — start blank.
        setCriterionLevels({});
        setOverallMark("");
        setFeedbackText("");
        lastSavedRef.current = { criterionLevels: {}, overallMark: "", feedbackText: "" };
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assessment?.id, studentNumber]);

  // ---- Save helpers (criterion levels + mark save instantly; feedback debounces) ----
  const saveCriterionLevel = useCallback(
    async (crit, level) => {
      const next = { ...criterionLevels };
      if (!level) delete next[crit];
      else next[crit] = level;
      setCriterionLevels(next);
      try {
        const res = await upsertGradebookResult({
          assessment_id: assessment.id,
          student_number: studentNumber,
          criterion_levels: next,
        });
        if (res?.result && onSaved) onSaved(res.result);
      } catch (e) {
        toast.error("Couldn't save that criterion mark.");
      }
    },
    [criterionLevels, assessment, studentNumber, onSaved]
  );

  const saveOverallMark = useCallback(
    async (mark) => {
      setOverallMark(mark || "");
      try {
        const res = await upsertGradebookResult({
          assessment_id: assessment.id,
          student_number: studentNumber,
          mark: mark || null,
        });
        if (res?.result && onSaved) onSaved(res.result);
      } catch (e) {
        toast.error("Couldn't save the overall mark.");
      }
    },
    [assessment, studentNumber, onSaved]
  );

  const debouncedSaveFeedback = useMemo(
    () =>
      debounce(async (text) => {
        try {
          const res = await upsertGradebookResult({
            assessment_id: assessment.id,
            student_number: studentNumber,
            feedback_text: text,
          });
          if (res?.result && onSaved) onSaved(res.result);
        } catch (e) {
          toast.error("Couldn't save feedback.");
        }
      }, 700),
    [assessment, studentNumber, onSaved]
  );

  useEffect(() => () => debouncedSaveFeedback.cancel?.(), [debouncedSaveFeedback]);

  const handleFeedbackChange = (val) => {
    setFeedbackText(val);
    debouncedSaveFeedback(val);
  };

  const handleGenerateFeedback = async () => {
    if (Object.keys(criterionLevels).length === 0 && !overallMark) {
      toast.error("Mark at least one criterion before generating feedback.");
      return;
    }
    setAiBusy(true);
    try {
      const res = await generateGradebookFeedback({
        assessment_id: assessment.id,
        student_number: studentNumber,
        tone: aiTone,
      });
      if (res?.draft) {
        setFeedbackText(res.draft);
        debouncedSaveFeedback(res.draft);
        toast.success("Draft ready — edit it to suit.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't generate a draft.";
      toast.error(detail);
    } finally {
      setAiBusy(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!assessment?.id || !studentNumber) return;
    const url = gradebookRubricPdfUrl(assessment.id, studentNumber);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const dateStr = useMemo(() => {
    const d = assessment?.date;
    if (!d) return "";
    try {
      const dt = new Date(d + "T00:00:00");
      return dt.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return d;
    }
  }, [assessment?.date]);

  if (!assessment) return null;

  const criteriaList = assessment.success_criteria || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // ---- UNBRANDED CONTAINER ----
        // No slate-navy or mint chrome. Pure white card, neutral greys.
        className="max-w-2xl w-[calc(100vw-1.5rem)] max-h-[92vh] overflow-y-auto bg-white p-0 border border-neutral-300 shadow-xl rounded-md"
        style={{ fontFamily: '"Montserrat", system-ui, sans-serif' }}
        data-testid="rubric-panel"
      >
        {/* a11y — hidden title + description so dialog announces correctly */}
        <DialogTitle className="sr-only">
          Rubric for {studentLabel || `Student ${studentNumber}`} — {assessment.assessment_name}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Per-student rubric panel with criterion levels, holistic overall mark, and feedback.
        </DialogDescription>

        {/* ---- Title block ---- */}
        <div className="px-6 pt-6 pb-3 border-b border-neutral-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2
                className="text-2xl font-extrabold text-neutral-900 leading-tight tracking-tight"
                data-testid="rubric-panel-task"
              >
                {assessment.assessment_name}
              </h2>
              <p className="text-[12px] text-neutral-600 mt-1">
                <span
                  className="font-extrabold text-neutral-800"
                  data-testid="rubric-panel-student"
                >
                  {studentLabel || `Student ${studentNumber}`}
                </span>
                {assessment.subject ? (
                  <>  ·  {assessment.subject}</>
                ) : null}
                {dateStr ? <>  ·  {dateStr}</> : null}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 shrink-0"
              aria-label="Close"
              data-testid="rubric-panel-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-10 flex items-center gap-2 text-neutral-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading rubric…
          </div>
        ) : (
          <>
            {/* ---- Overall mark ---- */}
            <div className="px-6 pt-5 pb-4 border-b border-neutral-200">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-neutral-500">
                Overall mark
              </p>
              <div className="flex items-end gap-3 mt-1">
                <select
                  value={overallMark || ""}
                  onChange={(e) => saveOverallMark(e.target.value)}
                  data-testid="rubric-panel-overall-mark"
                  className="appearance-none bg-white border border-neutral-300 hover:border-neutral-500 focus:border-neutral-700 focus:outline-none rounded-md px-3 py-2 text-2xl font-extrabold text-neutral-900 min-w-[90px]"
                >
                  <option value="">—</option>
                  {ONTARIO_GRANULAR.map((lv) => (
                    <option key={lv} value={lv}>
                      {lv}
                    </option>
                  ))}
                  <option value="needs_review">Needs review</option>
                </select>
                <p className="text-[11px] italic text-neutral-500 leading-snug pb-2 max-w-md">
                  Your holistic judgment. The criteria below inform this mark —
                  the platform never averages them into it.
                </p>
              </div>
            </div>

            {/* ---- Criteria rows ---- */}
            <div className="px-6 py-4">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-neutral-500 mb-3">
                Criteria
              </p>
              {criteriaList.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">
                  No criteria recorded on this assessment.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {criteriaList.map((crit, idx) => {
                    const marked = criterionLevels[crit] || "";
                    return (
                      <li
                        key={idx}
                        className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-center"
                        data-testid={`rubric-row-${idx}`}
                      >
                        <p className="text-[14px] text-neutral-800 leading-snug">
                          {crit}
                        </p>
                        <div className="flex items-center gap-1.5">
                          {RUBRIC_LEVELS.map((lv) => {
                            const isMarked = marked === lv;
                            return (
                              <button
                                key={lv}
                                type="button"
                                onClick={() =>
                                  saveCriterionLevel(crit, isMarked ? "" : lv)
                                }
                                aria-pressed={isMarked}
                                data-testid={`rubric-row-${idx}-level-${lv}`}
                                className={`h-10 w-12 rounded-md text-base font-extrabold border transition-colors ${
                                  isMarked
                                    ? "bg-neutral-900 text-white border-neutral-900"
                                    : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-700 hover:text-neutral-900"
                                }`}
                              >
                                {lv}
                              </button>
                            );
                          })}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* ---- Feedback box + AI assist ---- */}
            <div className="px-6 pb-6 pt-2 border-t border-neutral-200">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-neutral-500">
                  Feedback
                </p>
                <div className="flex items-center gap-2">
                  <div
                    className="flex items-center rounded-md border border-neutral-300 overflow-hidden text-[11px]"
                    data-testid="rubric-panel-tone-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => setAiTone("parent")}
                      data-testid="rubric-panel-tone-parent"
                      className={`px-2.5 py-1 font-extrabold ${
                        aiTone === "parent"
                          ? "bg-neutral-900 text-white"
                          : "bg-white text-neutral-600 hover:text-neutral-900"
                      }`}
                    >
                      Parent
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiTone("student")}
                      data-testid="rubric-panel-tone-student"
                      className={`px-2.5 py-1 font-extrabold border-l border-neutral-300 ${
                        aiTone === "student"
                          ? "bg-neutral-900 text-white"
                          : "bg-white text-neutral-600 hover:text-neutral-900"
                      }`}
                    >
                      Student
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateFeedback}
                    disabled={aiBusy}
                    data-testid="rubric-panel-ai-feedback"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-400 text-white text-[11px] font-extrabold"
                  >
                    {aiBusy ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Drafting…
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3.5 w-3.5" />
                        Generate feedback draft
                      </>
                    )}
                  </button>
                </div>
              </div>
              <textarea
                value={feedbackText}
                onChange={(e) => handleFeedbackChange(e.target.value)}
                placeholder="Comment on this student's work — strengths, areas to grow, next step."
                rows={5}
                data-testid="rubric-panel-feedback"
                className="w-full bg-white border border-neutral-300 focus:border-neutral-700 focus:outline-none rounded-md px-3 py-2.5 text-[13.5px] text-neutral-900 leading-relaxed resize-y"
                style={{ fontFamily: '"Montserrat", system-ui, sans-serif' }}
              />
              <p className="text-[10.5px] text-neutral-500 mt-1.5 italic">
                The draft is a starting point — edit freely. The final comment
                is yours.
              </p>
            </div>

            {/* ---- Footer actions ---- */}
            <div className="px-6 pb-6 pt-2 border-t border-neutral-200 flex items-center justify-between flex-wrap gap-3">
              <p className="text-[10.5px] text-neutral-500">
                Auto-saving as you mark.
              </p>
              <button
                type="button"
                onClick={handleDownloadPdf}
                data-testid="rubric-panel-export-pdf"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white text-[11.5px] font-extrabold"
              >
                <Download className="h-3.5 w-3.5" />
                Export PDF
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
